import { useApiClient } from 'src/hooks/useApiClient';
// @ts-ignore
import './StellantisQuotesHistoryPage.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import ServerPaginatedTable from 'src/components/ServerPaginatedTable/ServerPaginatedTable';
import { columnsData } from './columnsData';
import { StellantisQuoteRequestDTO } from 'src/api/models/StellantisDTO';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { FloatingToolbar, StellantisQuoteDetailOverlay, StellantisUtils, useStellantisQuoteAttachments } from 'src/components/Stellantis';
import { FaRegFilePdf } from 'react-icons/fa';
import ToolTip from 'src/components/Tooltip/Tooltip';
import { MdInfoOutline } from 'react-icons/md';
import { LuFileSymlink } from "react-icons/lu";
import { downloadCSV, downloadXLSX } from 'src/utils';
import { buildExportData } from './exportUtils';
import { GTMsendEvent } from 'src/utils/trackingGA4';
import { FileExportMenu } from 'src/components/FileExportMenu/FileExportMenu';
import { SortingRule } from 'react-table';

const PAGE_SIZE = 10;

type FilterQuery = {
  search: string;
  orderBy?: string;
  ascending?: boolean;
};

const INIT_FILTER: FilterQuery = {
  search: '',
  orderBy: undefined,
  ascending: undefined,
};

const StellantisQuotesHistoryPage = () => {
  const { apiClient, handleApi } = useApiClient();
  const [response, setResponse] = useState<StellantisQuoteRequestDTO[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<any>(null);
  const { showError } = useErrorToast();
  const [filterQuery, setFilterQuery] = useState<FilterQuery>(INIT_FILTER);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [selectedQuote, setSelectedQuote] = useState<StellantisQuoteRequestDTO | null>(null);
  const [showQuoteDetail, setShowQuoteDetail] = useState(false);
  const { downloadState, downloadExternalPdf, downloadHmpPdf } = useStellantisQuoteAttachments();
  const [tableKey, setTableKey] = useState(0);

  const refreshTable = useCallback(async (
    filters: FilterQuery,
    pagination = { skip: 0, limit: pageSize },
  ) => {
    setIsBusy(true);
    try {
      const res = await handleApi(apiClient.stellantisControllerService.getQuotesHistory({
        skip: pagination.skip,
        limit: pagination.limit,
        search: filters.search || undefined,
        orderBy: filters.orderBy,
        ascending: filters.ascending,
      }));
      setResponse(StellantisUtils.mapQuoteRequests(res.data));
      setTotalCount(res.totalCount);
      setError(null);
    } catch (err) {
      setError(err);
      showError(err, i18next.t('error_fetching_data'));
    } finally {
      setIsBusy(false);
      setLoading(false);
    }
  }, [apiClient, handleApi, showError, pageSize]);

  useEffect(() => { refreshTable(INIT_FILTER); }, []);

  const handleSearch = useCallback((e: { preventDefault: () => void }) => {
    e.preventDefault();
    refreshTable(filterQuery);
  }, [filterQuery, refreshTable]);

  const handleReset = useCallback(() => {
    setFilterQuery(INIT_FILTER);
    setTableKey(k => k + 1);
    refreshTable(INIT_FILTER);
  }, [refreshTable]);

  const handleSortChange = useCallback((rules: SortingRule<Object>[]) => {
    const orderBy = rules?.[0]?.id;
    const ascending = rules?.[0] ? !rules[0].desc : undefined;

    if (filterQuery.orderBy === orderBy && filterQuery.ascending === ascending) return;

    const newFilter = { ...filterQuery, orderBy, ascending };
    setFilterQuery(newFilter);
    refreshTable(newFilter);
  }, [filterQuery, refreshTable]);

  const renderActions = useCallback(({ row }) => {
    const isQuoted = StellantisUtils.isQuoted(row?.original);

    if (StellantisUtils.isUnquotable(row?.original)) {
      const anchorId = `unquotable_reason_${row.id}`;
      return (
        <Button
          id={anchorId}
          key={anchorId}
          className="fw-bold p-0"
          variant="link"
        >
          <ToolTip
            anchorId={anchorId}
            className="xed"
            place="left"
            content={row?.original?.unquotableReason}
          />
          <MdInfoOutline size={24} className="text-danger p-0 m-0" />
        </Button>
      )
    }

    if (!isQuoted) return <></>;

    const items = [
      <Button
        id={`download_hmp_pdf_${row.id}`}
        disabled={downloadState.id === row.original.id}
        onClick={() => downloadHmpPdf(row.original)}
        variant="link"
        className="text-white"
      >
        <FaRegFilePdf size={24} />
        <ToolTip
          anchorId={`download_hmp_pdf_${row.id}`}
          content={i18next.t('download_hmp_pdf')}
          className="xed"
          place="left"
        />
      </Button>,
      <Button
        id={`download_quote_external_pdf_${row.id}`}
        disabled={downloadState.id === row.original.id}
        onClick={() => downloadExternalPdf(row.original)}
        variant="link"
        className="text-white"
      >
        <LuFileSymlink size={24} />
        <ToolTip
          anchorId={`download_quote_external_pdf_${row.id}`}
          content={i18next.t('download_quote_external_pdf')}
          className="xed"
          place="left"
        />
      </Button>,
    ];

    return <FloatingToolbar items={items} />;
  }, [downloadState, downloadHmpPdf, downloadExternalPdf]);

  const columns = useMemo(() => {
    return columnsData.map((column) => {
      if (column.id === 'actions') {
        column.Cell = renderActions;
      }
      return column;
    });
  }, [renderActions]);

  const data = useMemo(() => response ?? [], [response]);

  const fetchAllForExport = useCallback(async (): Promise<StellantisQuoteRequestDTO[]> => {
    const EXPORT_BATCH_SIZE = 100;
    const all: StellantisQuoteRequestDTO[] = [];
    let skip = 0;
    let fetchedTotal = 0;

    do {
      const res = await handleApi(
        apiClient.stellantisControllerService.getQuotesHistory({
          skip,
          limit: EXPORT_BATCH_SIZE,
          search: filterQuery.search || undefined,
          orderBy: filterQuery.orderBy,
          ascending: filterQuery.ascending,
        })
      );
      all.push(...StellantisUtils.mapQuoteRequests(res.data));
      fetchedTotal = res.totalCount;
      skip += EXPORT_BATCH_SIZE;

      if (all.length >= fetchedTotal || res.data.length === 0) break;
    } while (all.length < fetchedTotal);

    return all;
  }, [apiClient, handleApi, filterQuery]);

  const handleExportButton = useCallback(async (format: string) => {
    if (!totalCount) {
      showError(null, i18next.t('no_data_available_for_download'));
      return;
    }

    let dataToExport = data;

    if (totalCount > data.length) {
      try {
        setIsBusy(true);
        dataToExport = await fetchAllForExport();
      } catch (err) {
        showError(err, i18next.t('error_fetching_data'));
        return;
      } finally {
        setIsBusy(false);
      }
    }

    const csvData = buildExportData(dataToExport);
    const fileName = i18next.t('stellantis_quotes_history').replace(/ /g, '_');

    switch (format) {
      case 'xlsx':
        downloadXLSX(csvData, fileName);
        break;
      case 'csv':
        downloadCSV(csvData, fileName);
        break;
      default:
        console.warn(`[Horizon] Unsupported export format: ${format}`);
        break;
    }

    GTMsendEvent({
      event: 'CTA_download_file',
      evtCat: 'storico_quotazioni',
      evtAct: 'download',
      evtLab: `${fileName}.${format}`,
    });
  }, [data, totalCount, fetchAllForExport, showError]);

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md={6} className="display-6">
          {i18next.t('stellantis_quotes_history')}
        </Col>
        <Col md={6} className="mt-4 mt-md-0 text-md-end">
          <FileExportMenu
            isDisabled={loading || isBusy}
            allowedFormats={['csv', 'xlsx']}
            handleExport={handleExportButton}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Row className="align-items-end">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Control
                        type="search"
                        placeholder={i18next.t('search')}
                        value={filterQuery.search}
                        onChange={({ target }) =>
                          setFilterQuery((prev) => ({ ...prev, search: target.value }))
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="text-end">
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      className="me-2"
                      disabled={isBusy}
                    >
                      {i18next.t('reset')}
                    </Button>
                    <Button type="submit" disabled={isBusy || !filterQuery.search}>
                      {isBusy ? <Spinner animation="border" size="sm" /> : i18next.t('search')}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <ServerPaginatedTable
        key={`${tableKey}-${pageSize}`}
        columns={columns}
        data={data}
        loading={loading || isBusy}
        error={error}
        totalCount={totalCount}
        pageSize={pageSize}
        showPageSelector
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          refreshTable(filterQuery, { skip: 0, limit: newSize });
        }}
        onSortChange={handleSortChange}
        loadFn={(skip, limit) => refreshTable(filterQuery, { skip, limit })}
      />

      {selectedQuote && (
        <StellantisQuoteDetailOverlay
          open={showQuoteDetail}
          quoteRequest={selectedQuote}
          onOpenChange={(bool) => {
            setShowQuoteDetail(bool);
            !bool && setSelectedQuote(null);
          }}
        />
      )}
    </Container>
  );
};

export default StellantisQuotesHistoryPage;
